import { Code, ConnectError, type Interceptor } from "@connectrpc/connect";

export type AuthenticationRecoveryHandler = () => Promise<void>;

let authenticationRecoveryHandler: AuthenticationRecoveryHandler = async () =>
  undefined;

export function configureAuthenticationRecoveryHandler(
  handler: AuthenticationRecoveryHandler,
): () => void {
  authenticationRecoveryHandler = handler;
  return () => {
    if (authenticationRecoveryHandler === handler)
      authenticationRecoveryHandler = async () => undefined;
  };
}

export const authenticationRecoveryInterceptor: Interceptor =
  (next) => async (request) => {
    try {
      return await next(request);
    } catch (cause) {
      if (
        cause instanceof ConnectError &&
        cause.code === Code.Unauthenticated
      ) {
        try {
          await authenticationRecoveryHandler();
        } catch {
          // Preserve the authoritative API rejection when local recovery fails.
        }
      }
      throw cause;
    }
  };

export function credentialedBrowserApiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(
    input instanceof Request ? input.headers : undefined,
  );
  new Headers(init.headers).forEach((value, name) => headers.set(name, value));
  headers.delete("Authorization");
  return fetch(input, {
    ...init,
    credentials: "include",
    headers,
  });
}

// httpLink makes one procedure request at a time, so a 401 identifies a rejected
// session. Preserve the response for tRPC to decode after local recovery.
export async function credentialedWebsiteApiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const response = await credentialedBrowserApiFetch(input, init);
  if (response.status === 401) {
    try {
      await authenticationRecoveryHandler();
    } catch {
      // Preserve the API error when local recovery fails.
    }
  }
  return response;
}
